import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, throwError } from 'rxjs';
import { AppSettings } from '../app-settings';
import { CreateSeasonPassPayload, UpdateSeasonPassPayload } from '../interfaces/season';

@Injectable({
  providedIn: 'root'
})
export class SeasonPassService {

  constructor(private http: HttpClient) { }

  /**
   * Autoservicio: los abonos del propio usuario autenticado, de cualquier
   * compañía. Es la fuente de "Mis abonos" — el listado por compañía es la
   * vista de gestión y exige rol de admin.
   */
  getMySeasonPasses() {
    const url = `${AppSettings.API_URI_V2}/season-passes/mine`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Inicia el pago online del abono. La respuesta trae `renewal` y el
   * `tpv_response` con los datos firmados para Redsys.
   *
   * OJO: `id_product` todavía NO lo acepta el backend (StoreRenewalPaymentRequest
   * solo valida quantity y redsys_method) — hoy se ignora y el producto lo elige
   * ProductRepository::findForSeasonAndCategory con un `first()` sin orden. Con
   * varios productos en la misma categoría eso puede cobrar uno distinto del
   * elegido, así que esta pantalla no debe desplegarse hasta que lo soporte.
   */
  payOnline(companyId: number, seasonPassId: number, payload: { quantity?: number; redsys_method?: 'card' | 'xpay' | 'bizum'; id_product?: number }) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/season-passes/${seasonPassId}/payment/online`;
    return this.http.post<any>(url, payload).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Productos que puede pagar este abono: los de su temporada asignados a su
   * categoría (p. ej. con y sin carnet físico). Es el equivalente de gestión
   * a `getProductsBySeason`, pero acotado al abono concreto y comprobando
   * permiso de la compañía.
   *
   * Lo usan los dos canales, así que por defecto devuelve lo que se vende por
   * web. `includeAdminOnly` añade los productos de canal panel (invitaciones,
   * descuentos), y el backend solo lo atiende si quien pregunta es staff de la
   * compañía: ser el dueño del abono no basta.
   */
  getAvailableProducts(companyId: number, seasonPassId: number, includeAdminOnly: boolean = false) {
    const query = includeAdminOnly ? '?include_admin_only=1' : '';
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/season-passes/${seasonPassId}/payment/products${query}`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Cobro TPV-directo (efectivo o datáfono en taquilla). Solo admin/superadmin
   * de la compañía. No pasa por la pasarela: el backend registra la renovación
   * como pagada al momento, así que no hay retorno que confirmar.
   */
  payDirect(companyId: number, seasonPassId: number, payload: { quantity?: number; id_product?: number }) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/season-passes/${seasonPassId}/payment/tpv-direct`;
    return this.http.post<any>(url, payload).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Confirma el pago al volver de la pasarela. El backend verifica contra el
   * TPV antes de activar el abono: un 422 significa "el TPV aún no lo
   * confirma", no un error definitivo.
   */
  confirmPayment(payload: { id_invoicing: string; num_ticket: string }) {
    const url = `${AppSettings.API_URI_V2}/season-passes/payment/confirm`;
    return this.http.put<any>(url, payload).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Enlace "Añadir a Google Wallet" del propio abono activo. El backend
   * devuelve `data.save_url` con la URL de guardado (JWT firmado). Un 503
   * significa que Wallet aún no está configurado en el servidor.
   */
  getGoogleWalletUrl(seasonPassId: number) {
    const url = `${AppSettings.API_URI_V2}/season-passes/${seasonPassId}/wallet/google`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Equivalente de Apple. Devuelve `data.download_url`: una URL firmada de vida
   * corta que sirve el `.pkpass`. No se puede pedir el fichero por aquí — iOS
   * solo abre Wallet si llega por una navegación del navegador, que no lleva el
   * Bearer —, así que quien llame tiene que navegar a esa URL. Un 503 significa
   * que Wallet aún no está configurado en el servidor.
   */
  getAppleWalletUrl(seasonPassId: number) {
    const url = `${AppSettings.API_URI_V2}/season-passes/${seasonPassId}/wallet/apple`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Listado paginado de abonos de la compañía. La respuesta es el paginador de
   * Laravel (`data.data`, `data.current_page`, `data.last_page`) más un
   * `data.summary` con el recuento por estado de todos los abonos que casan con
   * los filtros — el listado ya no llega completo, así que ni el desplegable de
   * estado ni el aviso de pendientes de cobro se pueden calcular en el cliente.
   *
   * `status` va por slug (no por la PK de `season_pass_statuses`).
   */
  getSeasonPassesByCompany(companyId: number, filters?: { search?: string; id_season?: number; id_status?: number; status?: string }, page = 1, perPage?: number) {
    const params: string[] = [`page=${page}`];
    if (perPage) params.push(`per_page=${perPage}`);
    if (filters?.search) params.push(`search=${encodeURIComponent(filters.search)}`);
    if (filters?.id_season) params.push(`id_season=${filters.id_season}`);
    if (filters?.id_status) params.push(`id_status=${filters.id_status}`);
    if (filters?.status) params.push(`status=${encodeURIComponent(filters.status)}`);
    let url = `${AppSettings.API_URI_V2}/companies/${companyId}/season-passes`;
    if (params.length) url += `?${params.join('&')}`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  getSeasonPassById(companyId: number, seasonPassId: number) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/season-passes/${seasonPassId}`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Butacas ocupadas del espacio (abonos de plataforma + socios legacy), como
   * lista de `seats.id`, para pintarlas como no seleccionables en el mapa
   * visual del alta/edición/renovación de abonos. La respuesta trae además
   * `blocked_seat_ids` (bloqueos permanentes del recinto: PMR, cabinas,
   * butacas retiradas) y `own_seat_id` para preseleccionar la butaca propia.
   *
   * Dos formas de excluir esa butaca propia, excluyentes entre sí:
   *  - `excludePassId`: edición de un abono ya existente.
   *  - `memberNumber`: renovación de un socio migrado, cuya butaca todavía
   *    solo consta en el array legacy. INERTE desde el 31-08-2026: el
   *    parámetro se sigue enviando pero el backend lo ignora (está comentado
   *    en SeasonPassController::occupiedSeats), y solo lo usa
   *    legacy-renewal-form, que está desactivado. Se mantiene la firma para
   *    que ese componente siga compilando por si se reactiva.
   */
  getOccupiedSeats(companyId: number, spaceId: number, excludePassId?: number, memberNumber?: string) {
    let url = `${AppSettings.API_URI_V2}/companies/${companyId}/spaces/${spaceId}/season-pass-occupied-seats`;
    if (excludePassId) url += `?exclude_pass_id=${excludePassId}`;
    else if (memberNumber) url += `?member_number=${encodeURIComponent(memberNumber)}`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  createSeasonPass(companyId: number, payload: CreateSeasonPassPayload) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/season-passes`;
    return this.http.post<any>(url, payload).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  updateSeasonPass(companyId: number, seasonPassId: number, payload: UpdateSeasonPassPayload) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/season-passes/${seasonPassId}`;
    return this.http.put<any>(url, payload).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  deleteSeasonPass(companyId: number, seasonPassId: number) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/season-passes/${seasonPassId}`;
    return this.http.delete<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Pedidos (renovaciones) de abono de la compañía. Es el equivalente a los
   * pedidos de entradas, pero para los pagos de abonos de temporada: cada
   * fila es un intento de cobro, pagado o no.
   */
  /**
   * Listado paginado de pedidos (renovaciones) de la compañía: paginador de
   * Laravel más un `data.summary` con los totales (cobrado, pagados,
   * pendientes) de todo lo que casa con los filtros, no solo de la página.
   */
  getRenewalsByCompany(companyId: number, filters?: { search?: string; id_season?: number; id_status?: number; id_product?: number; status?: string; exclude_status?: string }, page = 1, perPage?: number) {
    const params: string[] = [`page=${page}`];
    if (perPage) params.push(`per_page=${perPage}`);
    if (filters?.search) params.push(`search=${encodeURIComponent(filters.search)}`);
    if (filters?.id_season) params.push(`id_season=${filters.id_season}`);
    if (filters?.id_status) params.push(`id_status=${filters.id_status}`);
    if (filters?.id_product) params.push(`id_product=${filters.id_product}`);
    // `status`/`exclude_status` van por short_name (los de config/statuses.php
    // en el backend), no por la PK de order_statuses.
    if (filters?.status) params.push(`status=${encodeURIComponent(filters.status)}`);
    if (filters?.exclude_status) params.push(`exclude_status=${encodeURIComponent(filters.exclude_status)}`);
    let url = `${AppSettings.API_URI_V2}/companies/${companyId}/season-pass-renewals`;
    if (params.length) url += `?${params.join('&')}`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  getRenewalById(companyId: number, renewalId: number) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/season-pass-renewals/${renewalId}`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }
}
